import {useCallback, useEffect, useRef, useState} from 'react';

import {ProcedureRunDataEntryMode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {
  ProcedureRunPlanDesignStateSummary,
  SetProcedureRunPlanDesignStateAction,
  setProcedureRunPlanDesignState,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';

import {PlanConditionsByMethodId} from '../design-state/planConditionState';

interface GetInitialPlanDesignStateParams {
  persistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined;
  serverInitialConditions: PlanConditionsByMethodId;
  serverInitialExperimentalInputs: ExperimentalInputs;
  templateMethodIds: Set<string>;
}

interface InitialPlanDesignState {
  compatiblePersistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined;
  initialConditions: PlanConditionsByMethodId;
  initialExperimentalInputs: ExperimentalInputs;
}

interface UsePlanDesignPersistenceParams {
  conditions: PlanConditionsByMethodId;
  dataEntryMode: ProcedureRunDataEntryMode;
  dispatch: (action: SetProcedureRunPlanDesignStateAction) => void;
  editStateIsEditing: boolean;
  experimentalInputs: ExperimentalInputs;
  initialIsDirty: boolean;
  procedureRunPlanId: string;
  studyId: string;
}

interface PersistedPlanDesignState {
  conditions: PlanConditionsByMethodId;
  dataEntryMode: ProcedureRunDataEntryMode;
  experimentalInputs: ExperimentalInputs;
  isDirty: boolean;
  isInEditMode: boolean;
}

interface UsePlanDesignPersistenceResult {
  isDirty: boolean;
  persistPlanDesignState: (state: PersistedPlanDesignState) => void;
  setIsDirty: (isDirty: boolean) => void;
}

function hasCompatiblePersistedMethodKeys(
  keyedByMethodId: Record<string, unknown>,
  methodIds: Set<string>
): boolean {
  const persistedMethodIds = Object.keys(keyedByMethodId);
  // Require an exact key-set match so that we never reuse persisted state
  // that is missing entries for current methods or contains obsolete ones.
  if (persistedMethodIds.length !== methodIds.size) {
    return false;
  }
  return persistedMethodIds.every((methodId) => methodIds.has(methodId));
}

function isCompatiblePersistedPlanDesignState(
  persistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined,
  methodIds: Set<string>
): persistedPlanDesignState is ProcedureRunPlanDesignStateSummary {
  return (
    persistedPlanDesignState != null &&
    hasCompatiblePersistedMethodKeys(persistedPlanDesignState.experimentalInputs, methodIds) &&
    hasCompatiblePersistedMethodKeys(persistedPlanDesignState.conditions, methodIds)
  );
}

/**
 * Selects compatible persisted values and refreshes draft condition IDs from the server.
 */
export function getInitialPlanDesignState({
  persistedPlanDesignState,
  serverInitialConditions,
  serverInitialExperimentalInputs,
  templateMethodIds,
}: GetInitialPlanDesignStateParams): InitialPlanDesignState {
  const compatiblePersistedPlanDesignState = isCompatiblePersistedPlanDesignState(
    persistedPlanDesignState,
    templateMethodIds
  )
    ? persistedPlanDesignState
    : undefined;
  const initialExperimentalInputs =
    compatiblePersistedPlanDesignState?.experimentalInputs ?? serverInitialExperimentalInputs;

  // Redux captures brand-new rows before the first save assigns ids, and nothing back-fills
  // those ids afterwards. Refresh each draft from the server row at the same method and local
  // index. Names can change between snapshots and are not unique.
  const initialConditions =
    compatiblePersistedPlanDesignState?.conditions == null
      ? serverInitialConditions
      : refreshDraftConditionIds(
          compatiblePersistedPlanDesignState.conditions,
          serverInitialConditions,
          templateMethodIds
        );

  return {compatiblePersistedPlanDesignState, initialConditions, initialExperimentalInputs};
}

/**
 * Copies server ids onto draft rows at the same method and local index.
 */
function refreshDraftConditionIds(
  persistedConditionsByMethodId: PlanConditionsByMethodId,
  serverConditionsByMethodId: PlanConditionsByMethodId,
  methodIds: Set<string>
): PlanConditionsByMethodId {
  const refreshedConditionsByMethodId: PlanConditionsByMethodId = {};
  methodIds.forEach((methodId) => {
    const persistedConditionRows = persistedConditionsByMethodId[methodId] ?? [];
    const serverConditionRows = serverConditionsByMethodId[methodId] ?? [];
    refreshedConditionsByMethodId[methodId] = persistedConditionRows.map((conditionRow, index) => {
      if (conditionRow.id != null) {
        return conditionRow;
      }
      const serverId = serverConditionRows[index]?.id;
      return serverId != null ? {...conditionRow, id: serverId} : conditionRow;
    });
  });
  return refreshedConditionsByMethodId;
}

/**
 * Tracks local plan-design dirtiness and synchronizes the current state to Redux.
 */
export default function usePlanDesignPersistence({
  conditions,
  dataEntryMode,
  dispatch,
  editStateIsEditing,
  experimentalInputs,
  initialIsDirty,
  procedureRunPlanId,
  studyId,
}: UsePlanDesignPersistenceParams): UsePlanDesignPersistenceResult {
  const [isDirty, setIsDirty] = useState(initialIsDirty);
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setIsDirty(true);
  }, [conditions, experimentalInputs, dataEntryMode]);

  const persistPlanDesignState = useCallback(
    (state: PersistedPlanDesignState) => {
      dispatch(setProcedureRunPlanDesignState(studyId, procedureRunPlanId, state));
    },
    [dispatch, procedureRunPlanId, studyId]
  );

  useEffect(() => {
    persistPlanDesignState({
      conditions,
      experimentalInputs,
      dataEntryMode,
      isInEditMode: editStateIsEditing,
      isDirty,
    });
  }, [conditions, dataEntryMode, editStateIsEditing, experimentalInputs, isDirty, persistPlanDesignState]);

  return {isDirty, persistPlanDesignState, setIsDirty};
}
